/**
 * Bestehende Rechnung einlesen (PARITAET-R3-3, TA-081/AD-155/AD-167): aus den Textzeilen einer PDF-Rechnung (pdf-text.ts)
 * Nummer, Datum, Leistungszeitraum, Zahlungsziel, Netto, Steuer, Brutto, Währung und Positionen als Vorschlag ablesen.
 * Deutsch und Englisch, deutsche („1.234,56“) wie englische („1,234.56“) Zahlen. Was sich nicht sicher lesen lässt, bleibt
 * leer; die Person prüft und bestätigt die Werte, die Datei bleibt das Original.
 */
import { extractPdfLines, type Inflate } from "./pdf-text.js";

export interface ImportedLine {
  description: string;
  quantity: number;
  unitCents: number;
  totalCents: number;
}

export interface InvoiceImport {
  number: string | null;
  issueDate: string | null;
  serviceFrom: string | null;
  serviceTo: string | null;
  dueDate: string | null;
  /** Zahlungsziel in Tagen (aus „zahlbar innerhalb von 14 Tagen“ oder Fälligkeit − Rechnungsdatum) */
  termDays: number | null;
  netCents: number | null;
  vatCents: number | null;
  vatRatePercent: number | null;
  grossCents: number | null;
  currency: string | null;
  lines: ImportedLine[];
  /** erkannte Namen aus einer mitgegebenen Liste (z. B. Marken der Agentur) */
  names: string[];
  /** Anzahl gelesener Textzeilen; 0 = vermutlich gescannt (nur Bild) */
  textLines: number;
}

const MONTHS: Record<string, number> = {
  januar: 1, jänner: 1, january: 1, jan: 1, februar: 2, february: 2, feb: 2, märz: 3, maerz: 3, march: 3, mar: 3, mär: 3, april: 4, apr: 4,
  mai: 5, may: 5, juni: 6, june: 6, jun: 6, juli: 7, july: 7, jul: 7, august: 8, aug: 8, september: 9, sep: 9, sept: 9, oktober: 10,
  october: 10, okt: 10, oct: 10, november: 11, nov: 11, dezember: 12, december: 12, dez: 12, dec: 12,
};
const pad = (n: number) => String(n).padStart(2, "0");
function iso(y: number, m: number, d: number): string | null {
  if (y < 100) y += 2000;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 2000 || y > 2100) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCDate() === d ? `${y}-${pad(m)}-${pad(d)}` : null;
}

const DATE_SRC = String.raw`(\d{4}-\d{2}-\d{2}|\d{1,2}[./]\d{1,2}[./]\d{2,4}|\d{1,2}\.?\s+[A-Za-zÄÖÜäöü]{3,9}\.?\s+\d{4}|[A-Za-z]{3,9}\.?\s+\d{1,2},?\s+\d{4})`;
/** Alle Daten einer Zeile als 'YYYY-MM-DD' (Tag vor Monat; „09/24/2026“ wird als Monat/Tag erkannt, wenn der Tag > 12 ist) */
export function datesIn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(new RegExp(DATE_SRC, "g"))) {
    const s = m[1]!;
    let r: string | null = null;
    let x: RegExpExecArray | null;
    if ((x = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s))) r = iso(+x[1]!, +x[2]!, +x[3]!);
    else if ((x = /^(\d{1,2})([./])(\d{1,2})\2(\d{2,4})$/.exec(s))) {
      const a = +x[1]!, b = +x[3]!, y = +x[4]!;
      r = x[2] === "/" && b > 12 && a <= 12 ? iso(y, a, b) : iso(y, b, a);
    } else if ((x = /^(\d{1,2})\.?\s+([A-Za-zÄÖÜäöü]{3,9})\.?\s+(\d{4})$/.exec(s))) {
      const mo = MONTHS[x[2]!.toLowerCase()];
      r = mo ? iso(+x[3]!, mo, +x[1]!) : null;
    } else if ((x = /^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(s))) {
      const mo = MONTHS[x[1]!.toLowerCase()];
      r = mo ? iso(+x[3]!, mo, +x[2]!) : null;
    }
    if (r) out.push(r);
  }
  return out;
}

/** Geldbeträge einer Zeile in Cent, von links nach rechts (ohne Prozentangaben, Daten und lange Ziffernfolgen wie IBAN) */
export function amountsIn(text: string): number[] {
  const clean = text.replace(new RegExp(DATE_SRC, "g"), " ").replace(/\b\d{1,3}(?:[.,]\d{1,2})?\s*%/g, " ");
  const out: number[] = [];
  for (const m of clean.matchAll(/(-?)(?:€|EUR|CHF|USD|\$|£)?\s?(\d{1,3}(?:[.'  ]\d{3})+(?:[.,]\d{2})|\d{1,3}(?:,\d{3})+(?:\.\d{2})|\d+[.,]\d{2})(?!\d)/g)) {
    const raw = m[2]!.replace(/['  ]/g, "");
    // Dezimaltrennzeichen: das letzte Komma bzw. der letzte Punkt vor genau zwei Ziffern
    const dec = /[.,](\d{2})$/.exec(raw);
    const intPart = dec ? raw.slice(0, -3).replace(/[.,]/g, "") : raw.replace(/[.,]/g, "");
    const cents = Number(intPart) * 100 + (dec ? Number(dec[1]) : 0);
    if (Number.isFinite(cents)) out.push(m[1] ? -cents : cents);
  }
  return out;
}

function currencyOf(all: string): string | null {
  if (/CHF|Fr\./.test(all)) return "CHF";
  if (/€|\bEUR\b/.test(all)) return "EUR";
  if (/\bUSD\b|\$/.test(all)) return "USD";
  if (/£|\bGBP\b/.test(all)) return "GBP";
  return null;
}

const NUMBER_LABEL = /(rechnungs[-\s]?(?:nr|nummer|no)\.?|rechnung\s+nr\.?|rechnung\s+(?=[A-Z0-9-]*\d)|invoice\s*(?:no\.?|number|#|nr\.?)|beleg[-\s]?nr\.?|gutschrift\s*nr\.?)\s*[:#]?\s*/i;
const TOTAL_WORDS = /(gesamtbetrag|rechnungsbetrag|gesamt\s*brutto|bruttobetrag|summe\s*brutto|brutto\s*gesamt|endbetrag|zu\s+zahlen|zahlbetrag|total\s*(?:amount|due)?|amount\s+due|grand\s+total|gesamtsumme|^gesamt\b|^summe\b)/i;
const NET_WORDS = /(netto|nettobetrag|summe\s*netto|zwischensumme|subtotal|net\s*(?:amount|total)?\b)/i;
const VAT_WORDS = /(umsatzsteuer|mehrwertsteuer|\bust\.?\b|\bmwst\.?\b|\bvat\b|\bmwst\b|\btax\b)/i;
const HEAD_WORDS = /(beschreibung|bezeichnung|leistung|position|artikel|description|item|service)/i;
const HEAD_NUM_WORDS = /(menge|anzahl|anz\.|qty|quantity|einzelpreis|preis|betrag|amount|summe|total|price)/i;

/** Zeilen einer Rechnung auswerten. `names`: bekannte Namen (z. B. Marken), die im Text gesucht werden. */
export function parseInvoiceLines(lines: readonly string[], opts: { names?: readonly string[]; lang?: "de" | "en" } = {}): InvoiceImport {
  const text = lines.map((l) => l.replace(/\s+/g, " ").trim());
  const all = text.join("\n");
  const r: InvoiceImport = {
    number: null, issueDate: null, serviceFrom: null, serviceTo: null, dueDate: null, termDays: null, netCents: null, vatCents: null,
    vatRatePercent: null, grossCents: null, currency: currencyOf(all), lines: [], names: [], textLines: lines.length,
  };
  // Nummer: nach der Beschriftung, auf derselben oder der nächsten Zeile
  for (let i = 0; i < text.length && !r.number; i++) {
    const m = NUMBER_LABEL.exec(text[i]!);
    if (!m) continue;
    const rest = text[i]!.slice(m.index + m[0].length);
    const cand = (/^([A-Z0-9][A-Za-z0-9\-/_.]{1,30})/.exec(rest) ?? /^([A-Z0-9][A-Za-z0-9\-/_.]{1,30})/.exec(text[i + 1] ?? ""))?.[1];
    if (cand && /\d/.test(cand) && !datesIn(cand).length) r.number = cand.replace(/[.,]$/, "");
  }
  const labelled = (re: RegExp): string | null => {
    for (let i = 0; i < text.length; i++) {
      const m = re.exec(text[i]!);
      if (!m) continue;
      const d = datesIn(text[i]!.slice(m.index)) ;
      if (d.length) return d[0]!;
      const next = datesIn(text[i + 1] ?? "");
      if (next.length) return next[0]!;
    }
    return null;
  };
  r.issueDate = labelled(/(rechnungsdatum|belegdatum|ausstellungsdatum|invoice\s+date|date\s+of\s+issue|^datum\b|\bdatum:|^date\b)/i);
  // Leistungszeitraum: „01.08.2026 – 31.08.2026“ oder „bis“; Leistungsdatum ein Tag; Leistungsmonat „August 2026“
  for (let i = 0; i < text.length; i++) {
    const m = /(leistungszeitraum|leistungsdatum|lieferdatum|leistungsmonat|service\s+(?:period|date)|delivery\s+date)/i.exec(text[i]!);
    if (!m) continue;
    const seg = `${text[i]!.slice(m.index)} ${datesIn(text[i]!).length ? "" : text[i + 1] ?? ""}`;
    const d = datesIn(seg);
    if (d.length) { r.serviceFrom = d[0]!; r.serviceTo = d[1] ?? d[0]!; break; }
    const mon = /([A-Za-zÄÖÜäöü]{3,9})\s+(\d{4})/.exec(seg.slice(m[0].length));
    const mo = mon ? MONTHS[mon[1]!.toLowerCase()] : undefined;
    if (mon && mo) {
      const y = +mon[2]!;
      r.serviceFrom = `${y}-${pad(mo)}-01`;
      r.serviceTo = `${y}-${pad(mo)}-${pad(new Date(Date.UTC(y, mo, 0)).getUTCDate())}`;
      break;
    }
  }
  r.dueDate = labelled(/(zahlbar\s+bis|fällig\s+(?:am|bis)|fälligkeit(?:sdatum)?|zahlungsziel|due\s+date|payable\s+(?:by|until)|due\s+on)/i);
  const term = /(?:innerhalb\s+(?:von\s+)?|within\s+|zahlbar\s+in\s+|netto\s+)(\d{1,3})\s*(?:tagen|tage|days)/i.exec(all);
  if (term) r.termDays = Number(term[1]);
  else if (r.dueDate && r.issueDate) {
    const days = Math.round((Date.parse(r.dueDate) - Date.parse(r.issueDate)) / 86_400_000);
    if (days >= 0 && days <= 180) r.termDays = days;
  }

  // Summen: letzter Betrag der Zeile mit der Beschriftung (auch die nächste Zeile, wenn die Beschriftung allein steht)
  let totalsFrom = text.length;
  const lastAmount = (i: number): number | null => {
    const a = amountsIn(text[i]!);
    if (a.length) return a[a.length - 1]!;
    const b = amountsIn(text[i + 1] ?? "");
    return b.length === 1 && !/[a-z]{4,}/i.test((text[i + 1] ?? "").replace(/eur|chf|usd/i, "")) ? b[0]! : null;
  };
  for (let i = 0; i < text.length; i++) {
    const l = text[i]!;
    if (VAT_WORDS.test(l) && !/(nr\.?|id|nummer|number|identifikation)\b/i.test(l.replace(/\d/g, ""))) {
      const rate = /(\d{1,2}(?:[.,]\d{1,2})?)\s*%/.exec(l);
      const a = lastAmount(i);
      if (a !== null && (rate || r.vatCents === null)) {
        r.vatCents = a;
        if (rate) r.vatRatePercent = Number(rate[1]!.replace(",", "."));
        totalsFrom = Math.min(totalsFrom, i);
      } else if (rate && r.vatRatePercent === null) r.vatRatePercent = Number(rate[1]!.replace(",", "."));
      continue;
    }
    if (NET_WORDS.test(l) && !TOTAL_WORDS.test(l.replace(NET_WORDS, ""))) {
      const a = lastAmount(i);
      if (a !== null) { r.netCents = a; totalsFrom = Math.min(totalsFrom, i); }
      continue;
    }
    if (TOTAL_WORDS.test(l)) {
      const a = lastAmount(i);
      if (a !== null) { r.grossCents = a; totalsFrom = Math.min(totalsFrom, i); }
    }
  }
  // Kleinunternehmer/Reverse Charge: keine Steuer
  if (r.vatCents === null && /§\s*19(\s*Abs\.?\s*1)?\s*UStG|section\s*19\s*\(1\)\s*UStG|kleinunternehmer|small\s+business\s+scheme|reverse\s+charge|steuerschuldnerschaft|nicht\s+steuerbar|not\s+subject\s+to\s+vat/i.test(all)) {
    r.vatCents = 0;
    r.vatRatePercent = 0;
  }
  if (r.netCents === null && r.grossCents !== null && r.vatCents !== null) r.netCents = r.grossCents - r.vatCents;
  if (r.grossCents === null && r.netCents !== null && r.vatCents !== null) r.grossCents = r.netCents + r.vatCents;
  if (r.vatCents === null && r.netCents !== null && r.grossCents !== null) r.vatCents = r.grossCents - r.netCents;
  if (r.netCents !== null && r.grossCents !== null && r.netCents === r.grossCents && r.vatCents === null) r.vatCents = 0;

  // Positionen: zwischen Tabellenkopf und den Summen die Zeilen mit Betrag am Ende; Folgezeilen ohne Betrag gehören zur Beschreibung
  const head = text.findIndex((l, i) => i < totalsFrom && HEAD_WORDS.test(l) && HEAD_NUM_WORDS.test(l));
  if (head >= 0) {
    for (let i = head + 1; i < totalsFrom; i++) {
      const raw = lines[i]!.trim();
      const a = amountsIn(raw);
      const cells = raw.split(/\s{2,}/).map((c) => c.trim()).filter(Boolean);
      if (!a.length) {
        const prev = r.lines[r.lines.length - 1];
        if (prev && raw.length < 160 && !/^(seite|page)\b/i.test(raw)) prev.description = `${prev.description} ${raw.replace(/\s+/g, " ")}`.trim();
        continue;
      }
      // Beschreibung: Text vor der ersten Zahl, ohne führende Positionsnummer
      let desc = cells.filter((c) => !/^[\d\s.,€%xX×-]+(EUR|CHF|USD|Stk\.?|pcs|h|Std\.?)?$/i.test(c)).join(" ");
      if (!desc) desc = raw.replace(/[\d.,€%]+/g, " ").replace(/\s+/g, " ").trim();
      desc = desc.replace(/^\d{1,3}[.)]?\s+/, "").replace(/\s+/g, " ").trim();
      if (!desc || /^(übertrag|carried forward)/i.test(desc)) continue;
      const total = a[a.length - 1]!;
      const unit = a.length >= 2 ? a[a.length - 2]! : total;
      // Menge: Zahl in einer eigenen Spalte (bzw. „2 x“), die mit Einzelpreis und Betrag aufgeht; die Positionsnummer davor
      // geht meist nicht auf. Sonst Betrag ÷ Einzelpreis, sonst 1.
      const qs = cells.map((c) => /^(\d{1,4}(?:[.,]\d{1,3})?)\s*(x|×|Stk\.?|pcs|Std\.?|h)?$/i.exec(c)).filter((m): m is RegExpExecArray => !!m && !/[.,]\d{2}$/.test(m[1]!))
        .map((m) => Number(m[1]!.replace(",", "."))).filter((q) => Number.isFinite(q) && q > 0);
      const fits = qs.filter((q) => Math.abs(q * unit - total) <= Math.max(1, q));
      let qty = fits.length ? fits[fits.length - 1]! : unit > 0 && a.length >= 2 ? Math.round((total / unit) * 1000) / 1000 : 1;
      if (!Number.isFinite(qty) || qty <= 0) qty = 1;
      const unitCents = Math.abs(qty * unit - total) <= Math.max(1, qty) ? unit : Math.round(total / qty);
      r.lines.push({ description: desc.slice(0, 500), quantity: qty, unitCents, totalCents: total });
    }
  }
  // Ohne erkennbare Positionen: eine Zeile mit dem Nettobetrag
  if (!r.lines.length && r.netCents !== null) {
    const what = opts.lang === "en" ? "Services as per invoice" : "Leistung laut Rechnung";
    r.lines.push({ description: r.number ? `${what} ${r.number}` : what, quantity: 1, unitCents: r.netCents, totalCents: r.netCents });
  }
  if (opts.names?.length) {
    const low = all.toLowerCase();
    r.names = opts.names.filter((n) => n.trim().length >= 3 && low.includes(n.trim().toLowerCase()));
  }
  return r;
}

/** PDF-Datei lesen und auswerten. */
export async function readInvoicePdf(bytes: Uint8Array, opts: { names?: readonly string[]; lang?: "de" | "en"; inflate?: Inflate } = {}): Promise<InvoiceImport> {
  const lines = await extractPdfLines(bytes, { inflate: opts.inflate });
  return parseInvoiceLines(lines, { names: opts.names, lang: opts.lang });
}
